import React, { useState, useEffect } from 'react';
import './Settings.css';
import { useAuth } from '../contexts/AuthContext';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable item for selected builds
function SortableSelectedBuild({ build, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: build.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li ref={setNodeRef} style={style} className={isDragging ? 'dragging' : ''}>
      <span className="drag-handle" {...attributes} {...listeners}>⋮⋮</span>
      <div className="build-text">
        <span className="build-name">{build.name}</span>
        <span className="project-name">{build.projectName}</span>
      </div>
      <button
        onClick={() => onRemove(build)}
        className="remove-button"
        title="Remove"
      >
        &times;
      </button>
    </li>
  );
}

function Settings({ teamcityApi, selectedBuilds, showPersonalBuilds, enableDebugging, showQueuedBuilds, darkMode, onDarkModeChange, onSave, onBack }) {
  const { isAdmin } = useAuth();
  const [buildTypes, setBuildTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null); // null, 'saving', 'saved'
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(selectedBuilds || []);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [showPersonal, setShowPersonal] = useState(showPersonalBuilds !== undefined ? showPersonalBuilds : false);
  const [debugging, setDebugging] = useState(enableDebugging !== undefined ? enableDebugging : false);
  const [showQueued, setShowQueued] = useState(showQueuedBuilds !== undefined ? showQueuedBuilds : true);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  useEffect(() => {
    loadBuildTypes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadBuildTypes() {
    try {
      setLoading(true);
      const types = await teamcityApi.getAllBuildTypes();
      setBuildTypes(types);
    } catch (err) {
      setError('Failed to load build types: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function savePreferences(newSelected, newShowPersonal, newDebugging, newShowQueued) {
    setSaveStatus('saving');
    try {
      await teamcityApi.saveUserPreferences(newSelected, newShowPersonal, newDebugging, newShowQueued);
      onSave(newSelected, newShowPersonal, newDebugging, newShowQueued);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 1000);
    } catch (err) {
      setError('Failed to save: ' + err.message);
      setSaveStatus(null);
    }
  }

  async function toggleBuild(buildType) {
    const exists = selected.find(b => b.id === buildType.id);
    let newSelected;
    if (exists) {
      newSelected = selected.filter(b => b.id !== buildType.id);
    } else {
      newSelected = [...selected, {
        id: buildType.id,
        name: buildType.name,
        projectName: buildType.projectName
      }];
    }
    setSelected(newSelected);
    await savePreferences(newSelected, showPersonal, debugging, showQueued);
  }

  async function toggleShowPersonal() {
    const newShowPersonal = !showPersonal;
    setShowPersonal(newShowPersonal);
    await savePreferences(selected, newShowPersonal, debugging, showQueued);
  }

  async function toggleDebugging() {
    const newDebugging = !debugging;
    setDebugging(newDebugging);
    await savePreferences(selected, showPersonal, newDebugging, showQueued);
  }

  async function toggleShowQueued() {
    const newShowQueued = !showQueued;
    setShowQueued(newShowQueued);
    await savePreferences(selected, showPersonal, debugging, newShowQueued);
  }

  async function handleReorder(event) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = selected.findIndex(b => b.id === active.id);
      const newIndex = selected.findIndex(b => b.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newSelected = arrayMove(selected, oldIndex, newIndex);
        setSelected(newSelected);
        await savePreferences(newSelected, showPersonal, debugging, showQueued);
      }
    }
  }

  function isSelected(buildTypeId) {
    return selected.some(b => b.id === buildTypeId);
  }

  async function loadAdmins() {
    setAdminLoading(true);
    setAdminError(null);
    try {
      const data = await teamcityApi.getAdminUsers();
      setAdminUsers(data.users || []);
    } catch (err) {
      setAdminError('Failed to load admins: ' + err.message);
    } finally {
      setAdminLoading(false);
    }
  }

  async function addAdmin(e) {
    e.preventDefault();
    const email = newAdminEmail.trim();
    if (!email) return;
    setAdminError(null);
    try {
      await teamcityApi.addAdminUser(email);
      setNewAdminEmail('');
      await loadAdmins();
    } catch (err) {
      setAdminError('Failed to add admin: ' + err.message);
    }
  }

  async function removeAdmin(email) {
    setAdminError(null);
    try {
      await teamcityApi.removeAdminUser(email);
      await loadAdmins();
    } catch (err) {
      setAdminError('Failed to remove admin: ' + err.message);
    }
  }

  useEffect(() => {
    if (isAdmin) {
      loadAdmins();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Group build types by project
  const groupedByProject = buildTypes.reduce((acc, bt) => {
    const project = bt.projectName || 'Unknown Project';
    if (!acc[project]) {
      acc[project] = [];
    }
    acc[project].push(bt);
    return acc;
  }, {});

  // Filter by search
  const filteredProjects = Object.entries(groupedByProject)
    .map(([project, builds]) => ({
      project,
      builds: builds.filter(b =>
        b.name.toLowerCase().includes(search.toLowerCase()) ||
        b.id.toLowerCase().includes(search.toLowerCase()) ||
        project.toLowerCase().includes(search.toLowerCase())
      )
    }))
    .filter(p => p.builds.length > 0)
    .sort((a, b) => a.project.localeCompare(b.project));

  return (
    <div className="settings">
      <div className="settings-header">
        <button onClick={onBack} className="back-button">
          &larr; Back to Dashboard
        </button>
        <h2>Build Configuration Settings</h2>
      </div>

      {error && <div className="settings-error">{error}</div>}
      {saveStatus && (
        <span className="saving-indicator">
          {saveStatus === 'saving' ? 'Settings Saving...' : 'Settings Saved'}
        </span>
      )}

      <div className="settings-content">
        <div className="settings-sidebar">
          <div className="sidebar-section">
            <h4 className="section-title">Settings</h4>
            <div className="settings-option">
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={darkMode}
                  onChange={() => onDarkModeChange(!darkMode)}
                />
                <span className="toggle-label">Dark Mode</span>
              </label>
              <p className="settings-help">Use dark theme (or follow system)</p>
            </div>

            <div className="settings-option">
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={showPersonal}
                  onChange={toggleShowPersonal}
                />
                <span className="toggle-label">Show Personal Builds</span>
              </label>
              <p className="settings-help">Include personal builds in the dashboard</p>
            </div>

            <div className="settings-option">
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={showQueued}
                  onChange={toggleShowQueued}
                />
                <span className="toggle-label">Show Build Queue</span>
              </label>
              <p className="settings-help">Show builds waiting to start</p>
            </div>

            <div className="settings-option">
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={debugging}
                  onChange={toggleDebugging}
                />
                <span className="toggle-label">Enable Debugging</span>
              </label>
              <p className="settings-help">Show REST API links for debugging</p>
            </div>
          </div>

          {isAdmin && (
            <div className="sidebar-section admin-section">
              <h4 className="section-title">Admin Management</h4>
              {adminError && <div className="settings-error">{adminError}</div>}
              {adminLoading ? (
                <p className="settings-help">Loading admins...</p>
              ) : (
                <ul className="admin-list">
                  {adminUsers.map(u => (
                    <li key={u.email} className="admin-item">
                      <span className="admin-email">{u.email}</span>
                      <button
                        className="remove-button"
                        onClick={() => removeAdmin(u.email)}
                        title="Remove admin"
                      >
                        &times;
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <form className="admin-add-form" onSubmit={addAdmin}>
                <input
                  type="email"
                  placeholder="user@example.com"
                  value={newAdminEmail}
                  onChange={e => setNewAdminEmail(e.target.value)}
                />
                <button type="submit" className="add-admin-button">Add</button>
              </form>
            </div>
          )}

          <div className="sidebar-section">
            <h4 className="section-title">Selected Build Configurations</h4>
            {selected.length === 0 ? (
              <p className="no-selection">No builds selected yet</p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleReorder}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              >
                <SortableContext
                  items={selected.map(b => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="selected-list">
                    {selected.map(build => (
                      <SortableSelectedBuild
                        key={build.id}
                        build={build}
                        onRemove={toggleBuild}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>

        <div className="settings-main">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search build configurations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="loading">Loading build configurations...</div>
          ) : (
            <div className="build-list">
              {filteredProjects.map(({ project, builds }) => (
                <div key={project} className="project-group">
                  <h4 className="project-header">{project}</h4>
                  <ul>
                    {builds.map(build => (
                      <li
                        key={build.id}
                        className={isSelected(build.id) ? 'selected' : ''}
                        onClick={() => toggleBuild(build)}
                      >
                        <span className="checkbox">
                          {isSelected(build.id) ? '✓' : ''}
                        </span>
                        <span className="build-info">
                          <span className="build-name">{build.name}</span>
                          <span className="build-id">{build.id}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;
